import { useState, useCallback } from 'react';

interface GpsPosition {
  latitude: number;
  longitude: number;
  accuracy: number;
}

interface UseGpsReturn {
  getCurrentPosition: () => Promise<GpsPosition>;
  isLoading: boolean;
  error: string | null;
  isSupported: boolean;
}

export function useGps(): UseGpsReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  const getCurrentPosition = useCallback((): Promise<GpsPosition> => {
    return new Promise((resolve, reject) => {
      if (!isSupported) {
        const err = 'Tu navegador no soporta geolocalizacion';
        setError(err);
        reject(new Error(err));
        return;
      }

      setIsLoading(true);
      setError(null);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setIsLoading(false);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (err) => {
          setIsLoading(false);
          let message: string;
          switch (err.code) {
            case err.PERMISSION_DENIED:
              message = 'Permiso de ubicacion denegado. Por favor, habilita la ubicacion en tu navegador.';
              break;
            case err.POSITION_UNAVAILABLE:
              message = 'No pudimos determinar tu ubicacion. Intenta de nuevo.';
              break;
            case err.TIMEOUT:
              message = 'La solicitud de ubicacion tardo demasiado. Intenta de nuevo.';
              break;
            default:
              message = 'No pudimos obtener tu ubicacion.';
          }
          setError(message);
          reject(new Error(message));
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  }, [isSupported]);

  return { getCurrentPosition, isLoading, error, isSupported };
}
